// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { isMediaMax, spacing, zIndex } from 'presentations/views/styles';
import dynamic from 'next/dynamic';
import { useRichEditor } from './hooks';
import DialogSubmitField from 'presentations/views/components/molecules/DialogSubmitField';
import TextField from '../TextField';
import Dialog from '../Dialog';

const ReactQuill: any = dynamic(
  async (): Promise<any> => {
    const { default: RQ } = await import('react-quill');
    await RQ.Quill.import('blots/embed');

    return ({ forwardedRef, ...props }: { forwardedRef: any }) => (
      <RQ ref={forwardedRef} {...props} />
    );
  },
  {
    ssr: false,
  },
);

export type RichEditorConfig = {
  formats: string[];
  toolbarContainer: (object | string)[] | false;
  imageAllowedTypes?: string[];
  imageUploadUrl?: string;
  fileUploadUrl?: string;
  readOnly?: boolean;
};

interface Props {
  content: string | null;
  config: RichEditorConfig;
  placeholder?: string | null;
  onChange?: any;
  onUploadError?: (errorMessage: string) => void;
  minHeight?: string | number;
  textAreaMinHeight?: string | number;
  textAreaMaxHeight?: string | number;
  isLoading?: boolean;
  zIndex?: number;
}

export default function RichEditor(props: Props) {
  const { state, actions } = useRichEditor({
    imageAllowedTypes: props.config.imageAllowedTypes ?? [],
    fileUploadUrl: props.config.fileUploadUrl,
    imageUploadUrl: props.config.imageUploadUrl,
    onUploadError: props.onUploadError,
  });
  if (typeof window === 'undefined') return null;
  const t = translateInComponent(translations);
  const isMobile = isMediaMax('tablet');

  const toolbar = props.config.toolbarContainer
    ? {
        handlers: {
          link: actions.onClickLinkTool,
          image: actions.onClickImageUploadTool,
          file: actions.onClickFileUploadTool,
        },
        container: props.config.toolbarContainer,
      }
    : false;

  const modules = {
    toolbar,
    clipboard: {
      matchVisual: false,
    },
  };
  const originalPlaceholderText = props.placeholder || t('placeholder');
  const placeholderText = isMobile
    ? `${originalPlaceholderText.substring(0, 22)}...`
    : originalPlaceholderText;

  const isLinkInsertSubmitDisabled = Boolean(!state.linkUrl || !state.linkText);

  return (
    <Container
      onDrop={actions.onDragFile}
      className="jicoo-editor-body"
      textAreaMinHeight={props.textAreaMinHeight}
      textAreaMaxHeight={props.textAreaMaxHeight}
    >
      <ReactQuill
        forwardedRef={state.editorRef}
        theme="snow"
        value={props.content}
        onChange={props.onChange}
        modules={modules}
        placeholder={placeholderText}
        formats={props.config.formats}
        readOnly={props.config.readOnly ?? false}
      />
      {props.isLoading && <LoadingView />}
      <StyledDialog
        isOpen={state.isShowLinkDialog}
        onClose={actions.onCancelLinkDialog}
        title={t('Link editor title')}
        isFillWidth
        actions={
          <DialogSubmitField
            onSubmit={actions.onSubmitLinkDialog}
            onCancel={actions.onCancelLinkDialog}
            isDisabled={isLinkInsertSubmitDisabled}
            submitLabel={t('Insert')}
          />
        }
        zIndex={props.zIndex}
      >
        <Block>
          <TextFieldWrap
            label={t('linkUrl')}
            name={'linkUrl'}
            value={state.linkUrl}
            isFullWidth={true}
            onChange={actions.onInputChange}
            placeholder={'https://www.jicoo.com'}
          />
          <TextFieldWrap
            label={t('linkText')}
            name={'linkText'}
            value={state.linkText}
            isFullWidth={true}
            onChange={actions.onInputChange}
            placeholder={'Jicoo'}
          />
        </Block>
      </StyledDialog>
    </Container>
  );
}

const Container = styled.div<{
  textAreaMinHeight?: string | number;
  textAreaMaxHeight?: string | number;
}>`
  position: relative;

  ${(props) =>
    props.textAreaMinHeight !== undefined &&
    css`
      .fr-box.fr-basic .fr-element {
        min-height: ${props.textAreaMinHeight}px !important;
      }
    `}
  ${(props) =>
    props.textAreaMaxHeight !== undefined &&
    css`
      .fr-box.fr-basic .fr-element {
        max-height: ${props.textAreaMaxHeight}px !important;
      }
    `}
  word-break: break-all;
`;

const LoadingView = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  background: #fff;
  opacity: 0.6;
  z-index: ${zIndex(100)};
`;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    width: 400px;
  }
`;
const Block = styled.div`
  margin-bottom: ${spacing(24)};
  :last-of-type {
    margin-bottom: 0;
  }
`;

const TextFieldWrap = styled(TextField)`
  width: 100%;
  margin-bottom: ${spacing(24)};
`;
